import { FC, ReactNode, useMemo } from 'react'
import { Box, Button, Typography } from '@mui/material'
import {
  LIGHT_COLORS_VARIANTS_OPACITY,
  PRIMARY_COLORS_VARIANTS,
  PRIMARY_COLORS_VARIANTS_OPACITY,
} from '@/utils/constants/colors'

export interface FilterElementProps {
  activeId: string | number | null
  id: number | string
  label: string
  icon?: ReactNode
  imageSrc?: string | null
  onClick: (id: string | number) => void
  isTournament?: boolean
}

export const FilterElement: FC<FilterElementProps> = ({
  activeId,
  id,
  label,
  icon,
  imageSrc,
  onClick,
  isTournament,
}) => {
  const isActiveElement = useMemo(() => {
    return activeId === id
  }, [activeId, id])

  const colorVariant = useMemo(() => {
    if (isActiveElement) {
      return {
        boxBgColor: PRIMARY_COLORS_VARIANTS_OPACITY[300],
        iconBgColor: isTournament ? 'transparent' : PRIMARY_COLORS_VARIANTS_OPACITY[300],
        boxBgColorHover: PRIMARY_COLORS_VARIANTS_OPACITY[300],
        textColor: PRIMARY_COLORS_VARIANTS[550],
      }
    }

    return {
      boxBgColor: 'transparent',
      boxBgColorHover: LIGHT_COLORS_VARIANTS_OPACITY[50],
      iconBgColor: isTournament ? 'transparent' : LIGHT_COLORS_VARIANTS_OPACITY[50],
      textColor: LIGHT_COLORS_VARIANTS_OPACITY[750],
    }
  }, [isActiveElement, isTournament])

  return (
    <Button
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        alignItems: 'center',
        justifyContent: 'center',
        textTransform: 'none',
        paddingX: '0.5rem',
        paddingY: '0.375rem',
        borderRadius: '0.75rem',
        bgcolor: colorVariant.boxBgColor,
        color: colorVariant.textColor,
        boxShadow: 'none',
        flexShrink: 0,
        '&:hover': {
          bgcolor: colorVariant.boxBgColorHover,
        },
      }}
      onClick={() => onClick(id)}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="2.25rem"
        height="2.25rem"
        borderRadius="100%"
        bgcolor={colorVariant.iconBgColor}
        color={colorVariant.textColor}
        sx={{
          '&:hover': {
            bgcolor: colorVariant.iconBgColor,
          },
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            fontSize: '1rem',
            lineHeight: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colorVariant.textColor,
          }}
        >
          {icon}

          {imageSrc && (
            <img
              src={imageSrc}
              alt={label}
              style={{
                width: '1.25rem',
                height: '1.25rem',
                objectFit: 'cover',
                borderRadius: '2px',
              }}
            />
          )}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        component="div"
        sx={{
          fontSize: '0.75rem',
          lineHeight: '1rem',
          color: colorVariant.textColor,
        }}
      >
        {label}
      </Typography>
    </Button>
  )
}
